import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CreateVendor from "./CreateVendor";
import EditVendor from "./EditVendor";
import {
  useListVendorsQuery,
  useDeleteVendorMutation,
  type Vendor,
} from "@/api/vendorApi";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Edit2, 
  Trash2, 
  ArrowRight,
  Plus
} from "lucide-react";

export default function VendorList() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  const {
    data: vendors = [],
    isLoading,
    isError,
    error,
  } = useListVendorsQuery();
  const [deleteVendor] = useDeleteVendorMutation();

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this vendor?")) {
      try {
        await deleteVendor(id).unwrap();
        toast.success("Vendor deleted successfully");
      } catch (err) {
        toast.error("Failed to delete vendor");
      }
    }
  };

  const handleViewClinics = (vendorId: number) => {
    navigate(`/super-admin/vendors/${vendorId}/clinics`);
  };

  const filteredVendors = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vendors.filter((vendor: Vendor) => {
      if (statusFilter !== "ALL" && String(vendor.status || "").toUpperCase() !== statusFilter) {
        return false;
      }
      if (!q) {
        return true;
      }

      const haystack = [
        String(vendor.vendorName || "").toLowerCase(),
        String(vendor.contactPersonName || "").toLowerCase(),
        String(vendor.contactEmail || "").toLowerCase(),
        String(vendor.contactPhone || "").toLowerCase(),
        String(vendor.city || "").toLowerCase(),
        String(vendor.state || "").toLowerCase(),
      ].join(" ");

      return haystack.includes(q);
    });
  }, [vendors, query, statusFilter]);

  if (isError || error) {
    return (
      <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-lg">
        <p className="text-destructive font-semibold">Error loading vendors</p>
        <p className="text-muted-foreground text-sm mt-1">{JSON.stringify(error)}</p>
      </div>
    );
  }

  if (isLoading && vendors.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-20 animate-pulse">
        <Building2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">Loading vendors...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vendors</h1>
          <p className="text-muted-foreground">Manage your laboratory networks and diagnostic partners.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="shrink-0 gap-2">
              <Plus className="h-4 w-4" /> Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-4xl p-0 overflow-hidden">
            <DialogHeader className="p-6 border-b bg-muted/30">
              <DialogTitle className="text-xl">Create New Vendor</DialogTitle>
            </DialogHeader>
            <CreateVendor onClose={() => setCreateOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Vendors Card */}
      <Card className="border-none shadow-md overflow-hidden bg-card/50 backdrop-blur-sm">
        <CardContent className="p-0">
          {vendors.length > 0 && (
            <div className="p-4 border-b bg-muted/20">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {filteredVendors.length} of {vendors.length}
                </div>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <Input
                    placeholder="Search vendors"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="md:w-72"
                  />
                  <Select
                    value={statusFilter}
                    onValueChange={(val) => setStatusFilter(val as typeof statusFilter)}
                  >
                    <SelectTrigger className="h-10 w-[140px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent className="rounded-md">
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="INACTIVE">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {filteredVendors.length > 0 ? (
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-[250px]">Vendor Name</TableHead>
                  <TableHead>Contact info</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-center">Clinics</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {filteredVendors.map((vendor: Vendor) => (
                  <TableRow key={vendor.id} className="group transition-colors">
                    <TableCell>
                      <div 
                        className="flex flex-col cursor-pointer group-hover:text-primary transition-colors"
                        onClick={() => handleViewClinics(vendor.id)}
                      >
                        <span className="font-semibold">{vendor.vendorName}</span>
                        <span className="text-xs text-muted-foreground">ID: {vendor.id}</span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-col gap-1 text-sm">
                        <div className="flex items-center gap-1.5 font-medium">
                          {vendor.contactPersonName}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" /> {vendor.contactEmail}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" /> {vendor.contactPhone}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{vendor.city}, {vendor.state}</span>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-mono">
                        {vendor.locationCount ?? 0}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-center">
                      <Badge 
                        variant={
                          vendor.status === "ACTIVE" ? "default" : 
                          vendor.status === "INACTIVE" ? "destructive" : 
                          "secondary"
                        }
                        className={vendor.status === "ACTIVE" ? "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 border-emerald-500/20" : ""}
                      >
                        {vendor.status}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                          onClick={() => {
                            setSelectedVendor(vendor);
                            setEditOpen(true);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(vendor.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary"
                          onClick={() => handleViewClinics(vendor.id)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {/* ⭐ EMPTY STATE (WHEN NO VENDORS) ⭐ */}
          {vendors.length === 0 && !isLoading && (
            <div className="text-center py-20">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-muted rounded-full">
                  <Building2 className="h-12 w-12 text-muted-foreground/50" />
                </div>
              </div>
              <h3 className="text-xl font-semibold">No vendors found</h3>
              <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
                You haven't added any vendors yet. Start by adding your first laboratory partner.
              </p>
              <Button onClick={() => setCreateOpen(true)} className="mt-6 gap-2">
                <Plus className="h-4 w-4" /> Add First Vendor
              </Button>
            </div>
          )}

          {vendors.length > 0 && filteredVendors.length === 0 && !isLoading && (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold">No vendors match your filters</h3>
              <p className="text-muted-foreground mt-2">Try clearing the search or changing status.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-6 border-b bg-muted/30">
            <DialogTitle className="text-xl">Edit Vendor</DialogTitle>
          </DialogHeader>
          {selectedVendor && (
            <EditVendor
              vendor={selectedVendor}
              onClose={() => {
                setEditOpen(false);
                setSelectedVendor(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
